import { useState, useRef, useEffect } from "react";
import MainLayout from "@/components/feature/MainLayout";
import { materials } from "@/mocks/systemModules";
import MaterialAIDeep from "./components/MaterialAIDeep";

type ViewMode = "list" | "2d" | "3d" | "ai";

const deviceModels = [
  { id: "D001", name: "IC卡燃气表", model: "G2.5-IC", category: "计量设备", status: "正常", location: "朝阳区仓库A", warranty: "2028-01-01", maintainDate: "2026-06-01", stock: 1250, price: 285, supplier: "金卡智能", image: "https://public.readdy.ai/ai/img_res/d3931ccd3f961f817cadb053cc9879df.jpg" },
  { id: "D002", name: "超声波流量计", model: "DN50-US", category: "计量设备", status: "正常", location: "海淀区仓库B", warranty: "2027-06-01", maintainDate: "2026-03-01", stock: 86, price: 3200, supplier: "西门子", image: "https://readdy.ai/api/search-image?query=ultrasonic%20flow%20meter%20industrial%20measurement%20device%20silver%20metal%20white%20background%20clean%20product%20photo&width=200&height=200&seq=mat002&orientation=squarish" },
  { id: "D003", name: "球阀DN50", model: "Q41F-16C", category: "阀门", status: "维护中", location: "西城区仓库C", warranty: "2026-12-01", maintainDate: "2026-01-15", stock: 320, price: 156, supplier: "中核苏阀", image: "https://readdy.ai/api/search-image?query=ball%20valve%20DN50%20industrial%20pipe%20fitting%20metal%20chrome%20white%20background%20clean%20product%20photo&width=200&height=200&seq=mat003&orientation=squarish" },
  { id: "D004", name: "调压器", model: "RTZ-50/0.4", category: "调压设备", status: "正常", location: "东城区仓库A", warranty: "2029-03-01", maintainDate: "2026-09-01", stock: 45, price: 1850, supplier: "重庆耐德", image: "https://readdy.ai/api/search-image?query=gas%20pressure%20regulator%20industrial%20equipment%20yellow%20metal%20white%20background%20clean%20product%20photo&width=200&height=200&seq=mat004&orientation=squarish" },
  { id: "D005", name: "可燃气体探测器", model: "JT-B2000", category: "安全设备", status: "正常", location: "丰台区仓库B", warranty: "2027-08-01", maintainDate: "2026-05-01", stock: 560, price: 420, supplier: "霍尼韦尔", image: "https://readdy.ai/api/search-image?query=gas%20detector%20sensor%20safety%20equipment%20white%20plastic%20device%20white%20background%20clean%20product%20photo&width=200&height=200&seq=mat005&orientation=squarish" },
  { id: "D006", name: "PE管DN110", model: "SDR11-110", category: "管材配件", status: "正常", location: "石景山仓库A", warranty: "2035-01-01", maintainDate: "2026-12-01", stock: 2800, price: 68, supplier: "联塑管业", image: "https://readdy.ai/api/search-image?query=PE%20pipe%20plastic%20pipeline%20yellow%20gas%20pipe%20white%20background%20clean%20product%20photo&width=200&height=200&seq=mat006&orientation=squarish" },
];

const aiInsights = [
  { type: "warning", icon: "ri-error-warning-line", color: "text-amber-600", bg: "bg-amber-50", title: "库存预警", content: "球阀DN50库存320件，低于安全库存500件，建议立即补货，预计2周内消耗完毕。" },
  { type: "info", icon: "ri-calendar-check-line", color: "text-cyan-600", bg: "bg-cyan-50", title: "维护提醒", content: "超声波流量计DN50-US已超过维护周期，建议安排2026-04-15前完成年度维护检测。" },
  { type: "success", icon: "ri-shield-check-line", color: "text-green-600", bg: "bg-green-50", title: "质保分析", content: "本季度共有3类设备质保到期，建议提前3个月联系供应商续保，预计费用¥12.5万。" },
  { type: "danger", icon: "ri-tools-line", color: "text-red-600", bg: "bg-red-50", title: "维修建议", content: "调压器RTZ-50/0.4近3个月故障率上升15%，建议对该批次设备进行全面检查。" },
];

function Device2DView({ device }: { device: typeof deviceModels[0] }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    // Background
    ctx.fillStyle = "#f8fafc";
    ctx.fillRect(0, 0, w, h);

    // Grid
    ctx.strokeStyle = "rgba(0,0,0,0.05)";
    ctx.lineWidth = 1;
    for (let x = 0; x < w; x += 20) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
    for (let y = 0; y < h; y += 20) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }

    const cx = w / 2;
    const cy = h / 2;

    if (device.category === "计量设备") {
      // Gas meter 2D schematic
      ctx.fillStyle = "#e2e8f0";
      ctx.strokeStyle = "#64748b";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(cx - 60, cy - 80, 120, 160, 8);
      ctx.fill();
      ctx.stroke();

      // Display screen
      ctx.fillStyle = "#1e293b";
      ctx.beginPath();
      ctx.roundRect(cx - 45, cy - 65, 90, 40, 4);
      ctx.fill();
      ctx.fillStyle = "#22c55e";
      ctx.font = "bold 14px monospace";
      ctx.textAlign = "center";
      ctx.fillText("00123.45", cx, cy - 40);
      ctx.font = "8px sans-serif";
      ctx.fillStyle = "#94a3b8";
      ctx.fillText("m³", cx + 38, cy - 40);

      // IC card slot
      ctx.fillStyle = "#E8531A";
      ctx.beginPath();
      ctx.roundRect(cx - 20, cy - 15, 40, 6, 2);
      ctx.fill();
      ctx.fillStyle = "#64748b";
      ctx.font = "9px sans-serif";
      ctx.fillText("IC卡槽", cx, cy + 5);

      // Buttons
      [cx - 30, cx, cx + 30].forEach((bx, i) => {
        ctx.fillStyle = ["#E8531A", "#64748b", "#64748b"][i];
        ctx.beginPath();
        ctx.arc(bx, cy + 30, 8, 0, Math.PI * 2);
        ctx.fill();
      });

      // Pipe connections
      ctx.strokeStyle = "#94a3b8";
      ctx.lineWidth = 8;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(cx - 60, cy - 20);
      ctx.lineTo(cx - 90, cy - 20);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx + 60, cy - 20);
      ctx.lineTo(cx + 90, cy - 20);
      ctx.stroke();

      // Labels
      ctx.fillStyle = "#475569";
      ctx.font = "10px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("进气口", cx - 75, cy - 30);
      ctx.fillText("出气口", cx + 75, cy - 30);
      ctx.fillText(device.model, cx, cy + 65);
    } else if (device.category === "阀门") {
      // Ball valve 2D
      ctx.strokeStyle = "#64748b";
      ctx.lineWidth = 8;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(cx - 100, cy);
      ctx.lineTo(cx - 35, cy);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx + 35, cy);
      ctx.lineTo(cx + 100, cy);
      ctx.stroke();

      ctx.fillStyle = "#cbd5e1";
      ctx.strokeStyle = "#475569";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(cx, cy, 35, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = "#E8531A";
      ctx.beginPath();
      ctx.arc(cx, cy, 20, 0, Math.PI * 2);
      ctx.fill();

      // Handle
      ctx.fillStyle = "#475569";
      ctx.beginPath();
      ctx.roundRect(cx - 5, cy - 60, 10, 30, 3);
      ctx.fill();
      ctx.beginPath();
      ctx.roundRect(cx - 20, cy - 65, 40, 8, 3);
      ctx.fill();

      ctx.fillStyle = "#22c55e";
      ctx.font = "bold 10px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("开启", cx, cy + 5);
      ctx.fillStyle = "#475569";
      ctx.font = "10px sans-serif";
      ctx.fillText(device.model, cx, cy + 55);
    } else {
      // Generic device
      ctx.fillStyle = "#e2e8f0";
      ctx.strokeStyle = "#64748b";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(cx - 70, cy - 70, 140, 140, 12);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = "#E8531A";
      ctx.font = "32px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("⚙", cx, cy + 12);

      ctx.fillStyle = "#475569";
      ctx.font = "11px sans-serif";
      ctx.fillText(device.name, cx, cy + 50);
      ctx.font = "9px sans-serif";
      ctx.fillStyle = "#94a3b8";
      ctx.fillText(device.model, cx, cy + 65);
    }

    // Status indicator
    ctx.fillStyle = device.status === "正常" ? "#22c55e" : "#f59e0b";
    ctx.beginPath();
    ctx.arc(w - 20, 20, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#475569";
    ctx.font = "9px sans-serif";
    ctx.textAlign = "right";
    ctx.fillText(device.status, w - 30, 24);
  }, [device]);

  return <canvas ref={canvasRef} width={320} height={280} className="w-full rounded-xl" />;
}

function Device3DView({ device }: { device: typeof deviceModels[0] }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const angleRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = "#0f172a";
      ctx.fillRect(0, 0, w, h);

      const angle = angleRef.current;
      const cx = w / 2;
      const cy = h / 2;

      // 3D isometric-style drawing
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);

      if (device.category === "计量设备") {
        // 3D box meter
        const bw = 80, bh = 120, bd = 40;
        const ox = cx + cos * bd * 0.5;
        const oy = cy - sin * bd * 0.3;

        // Top face
        ctx.fillStyle = "#334155";
        ctx.beginPath();
        ctx.moveTo(ox - bw / 2, oy - bh / 2);
        ctx.lineTo(ox + bw / 2, oy - bh / 2);
        ctx.lineTo(ox + bw / 2 + cos * bd, oy - bh / 2 - sin * bd * 0.5);
        ctx.lineTo(ox - bw / 2 + cos * bd, oy - bh / 2 - sin * bd * 0.5);
        ctx.closePath();
        ctx.fill();

        // Front face
        const grad = ctx.createLinearGradient(ox - bw / 2, oy - bh / 2, ox + bw / 2, oy + bh / 2);
        grad.addColorStop(0, "#475569");
        grad.addColorStop(1, "#1e293b");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.moveTo(ox - bw / 2, oy - bh / 2);
        ctx.lineTo(ox + bw / 2, oy - bh / 2);
        ctx.lineTo(ox + bw / 2, oy + bh / 2);
        ctx.lineTo(ox - bw / 2, oy + bh / 2);
        ctx.closePath();
        ctx.fill();

        // Side face
        ctx.fillStyle = "#1e293b";
        ctx.beginPath();
        ctx.moveTo(ox + bw / 2, oy - bh / 2);
        ctx.lineTo(ox + bw / 2 + cos * bd, oy - bh / 2 - sin * bd * 0.5);
        ctx.lineTo(ox + bw / 2 + cos * bd, oy + bh / 2 - sin * bd * 0.5);
        ctx.lineTo(ox + bw / 2, oy + bh / 2);
        ctx.closePath();
        ctx.fill();

        // Screen glow
        ctx.fillStyle = "#0f172a";
        ctx.beginPath();
        ctx.roundRect(ox - 35, oy - 45, 70, 30, 3);
        ctx.fill();
        ctx.fillStyle = "#22c55e";
        ctx.font = "bold 11px monospace";
        ctx.textAlign = "center";
        ctx.fillText("00123.45", ox, oy - 25);

        // IC card slot glow
        ctx.fillStyle = "#E8531A";
        ctx.shadowColor = "#E8531A";
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.roundRect(ox - 15, oy - 5, 30, 4, 2);
        ctx.fill();
        ctx.shadowBlur = 0;

        // Pipe connections 3D
        ctx.strokeStyle = "#64748b";
        ctx.lineWidth = 10;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(ox - bw / 2, oy - 20);
        ctx.lineTo(ox - bw / 2 - 50, oy - 20);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(ox + bw / 2, oy - 20);
        ctx.lineTo(ox + bw / 2 + 50, oy - 20);
        ctx.stroke();

        // Pipe highlight
        ctx.strokeStyle = "#94a3b8";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(ox - bw / 2, oy - 24);
        ctx.lineTo(ox - bw / 2 - 50, oy - 24);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(ox + bw / 2, oy - 24);
        ctx.lineTo(ox + bw / 2 + 50, oy - 24);
        ctx.stroke();
      } else if (device.category === "阀门") {
        // 3D ball valve
        ctx.strokeStyle = "#64748b";
        ctx.lineWidth = 14;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(cx - 120, cy);
        ctx.lineTo(cx - 40, cy);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(cx + 40, cy);
        ctx.lineTo(cx + 120, cy);
        ctx.stroke();

        // Pipe highlight
        ctx.strokeStyle = "#94a3b8";
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(cx - 120, cy - 5);
        ctx.lineTo(cx - 40, cy - 5);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(cx + 40, cy - 5);
        ctx.lineTo(cx + 120, cy - 5);
        ctx.stroke();

        // Valve body
        const vGrad = ctx.createRadialGradient(cx - 10, cy - 10, 5, cx, cy, 40);
        vGrad.addColorStop(0, "#94a3b8");
        vGrad.addColorStop(1, "#334155");
        ctx.fillStyle = vGrad;
        ctx.beginPath();
        ctx.arc(cx, cy, 40, 0, Math.PI * 2);
        ctx.fill();

        // Inner ball
        const iGrad = ctx.createRadialGradient(cx - 8, cy - 8, 3, cx, cy, 22);
        iGrad.addColorStop(0, "#E8531A");
        iGrad.addColorStop(1, "#9a2d0a");
        ctx.fillStyle = iGrad;
        ctx.beginPath();
        ctx.arc(cx, cy, 22, 0, Math.PI * 2);
        ctx.fill();

        // Handle
        ctx.fillStyle = "#475569";
        ctx.beginPath();
        ctx.roundRect(cx - 6, cy - 75, 12, 38, 4);
        ctx.fill();
        ctx.beginPath();
        ctx.roundRect(cx - 25, cy - 80, 50, 10, 4);
        ctx.fill();

        ctx.fillStyle = "#22c55e";
        ctx.font = "bold 11px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("OPEN", cx, cy + 5);
      } else {
        // Generic 3D box
        const bw = 100, bh = 100, bd = 50;
        const ox = cx + cos * bd * 0.3;
        const oy = cy - sin * bd * 0.2;

        ctx.fillStyle = "#334155";
        ctx.beginPath();
        ctx.moveTo(ox - bw / 2, oy - bh / 2);
        ctx.lineTo(ox + bw / 2, oy - bh / 2);
        ctx.lineTo(ox + bw / 2 + cos * bd, oy - bh / 2 - sin * bd * 0.5);
        ctx.lineTo(ox - bw / 2 + cos * bd, oy - bh / 2 - sin * bd * 0.5);
        ctx.closePath();
        ctx.fill();

        const grad2 = ctx.createLinearGradient(ox - bw / 2, oy - bh / 2, ox + bw / 2, oy + bh / 2);
        grad2.addColorStop(0, "#475569");
        grad2.addColorStop(1, "#1e293b");
        ctx.fillStyle = grad2;
        ctx.beginPath();
        ctx.moveTo(ox - bw / 2, oy - bh / 2);
        ctx.lineTo(ox + bw / 2, oy - bh / 2);
        ctx.lineTo(ox + bw / 2, oy + bh / 2);
        ctx.lineTo(ox - bw / 2, oy + bh / 2);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = "#E8531A";
        ctx.shadowColor = "#E8531A";
        ctx.shadowBlur = 15;
        ctx.font = "28px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("⚙", ox, oy + 10);
        ctx.shadowBlur = 0;
      }

      // Rotating indicator
      ctx.fillStyle = "rgba(232,83,26,0.6)";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "left";
      ctx.fillText("3D旋转预览", 10, h - 10);

      angleRef.current += 0.008;
      animRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(animRef.current);
  }, [device]);

  return <canvas ref={canvasRef} width={320} height={280} className="w-full rounded-xl" />;
}

export default function MaterialPage() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("全部");
  const [inModal, setInModal] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [selectedDevice, setSelectedDevice] = useState<typeof deviceModels[0]>(deviceModels[0]);
  const [deviceView, setDeviceView] = useState<"2d" | "3d">("2d");
  const [aiReport, setAiReport] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [activeDeviceTab, setActiveDeviceTab] = useState<"info" | "maintain" | "warranty" | "history">("info");

  const categories = ["全部", "计量设备", "管材配件", "阀门", "安全设备", "调压设备", "通信设备"];
  const filtered = materials.filter(m => {
    const matchCat = category === "全部" || m.category === category;
    const matchSearch = !search || m.name.includes(search) || m.model.includes(search);
    return matchCat && matchSearch;
  });

  const lowStock = materials.filter(m => m.stock <= m.minStock).length;
  const totalValue = materials.reduce((s, m) => s + m.stock * m.price, 0);

  const generateAIReport = () => {
    setAiGenerating(true);
    setAiReport("");
    const prompt = aiPrompt || "综合分析";
    const text = `【AI材料管理分析报告】
分析维度：${prompt} | 生成时间：2026-04-10 14:30

一、库存健康度分析
• 当前库存总值：¥${(totalValue / 10000).toFixed(1)}万元
• 库存预警品类：${lowStock}类，占总品类${((lowStock / materials.length) * 100).toFixed(0)}%
• 重点关注：球阀DN50库存仅剩320件，低于安全库存500件，建议立即启动采购流程
• 超声波流量计库存86台，按当前消耗速度预计45天后告罄

二、设备维护状态分析
• 本季度需维护设备：3类，涉及设备约1,200台
• 超期维护风险：超声波流量计DN50-US已超过维护周期32天
• 维护成本预测：本季度预计维护费用¥8.5万元
• 建议优先级：P0-超声波流量计 > P1-调压器 > P2-球阀

三、质保管理分析
• 近6个月到期质保：3类设备，涉及金额约¥45万
• 建议提前3个月联系供应商：西门子（超声波流量计）、中核苏阀（球阀）
• 质保续签预算建议：¥12.5万元

四、供应商评估
• 主要供应商：金卡智能（A级）、西门子（A级）、霍尼韦尔（A级）
• 建议：对中核苏阀进行季度评估，近期交货延迟率上升至8%

五、AI优化建议
1. 建立动态库存预警机制，设置3级预警阈值
2. 对高频消耗品类（IC卡表、PE管）建立长期框架协议
3. 引入RFID资产追踪，提升设备全生命周期管理效率
4. 建议下季度采购预算：¥85万元（较上季度增加12%）`;

    let i = 0;
    const interval = setInterval(() => {
      if (i < text.length) {
        setAiReport(prev => prev + text[i]);
        i++;
      } else {
        clearInterval(interval);
        setAiGenerating(false);
      }
    }, 18);
  };

  return (
    <MainLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">材料管理</h1>
            <p className="text-sm text-gray-500 mt-0.5">设备2D/3D展示 · 维护质保管理 · AI智能分析</p>
          </div>
          <div className="flex gap-2">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {([
                { key: "list", label: "台账", icon: "ri-list-check-2" },
                { key: "2d", label: "2D展示", icon: "ri-layout-2-line" },
                { key: "3d", label: "3D展示", icon: "ri-box-3-line" },
                { key: "ai", label: "AI分析", icon: "ri-robot-2-line" },
              ] as const).map(v => (
                <button
                  key={v.key}
                  onClick={() => setViewMode(v.key)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${viewMode === v.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  <i className={v.icon}></i>
                  {v.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setInModal(true)}
              className="flex items-center gap-2 bg-[#E8531A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#d44a15] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-box-line"></i>入库登记
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "材料种类", value: materials.length, color: "text-gray-800", bg: "bg-gray-50", icon: "ri-archive-line" },
            { label: "库存预警", value: lowStock, color: "text-red-600", bg: "bg-red-50", icon: "ri-error-warning-line" },
            { label: "库存总值", value: `¥${(totalValue / 10000).toFixed(1)}万`, color: "text-[#E8531A]", bg: "bg-orange-50", icon: "ri-money-cny-circle-line" },
            { label: "本月入库", value: "23批次", color: "text-green-600", bg: "bg-green-50", icon: "ri-inbox-archive-line" },
          ].map((s, i) => (
            <div key={i} className={`${s.bg} rounded-xl p-4 flex items-center gap-3`}>
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white">
                <i className={`${s.icon} ${s.color} text-lg`}></i>
              </div>
              <div>
                <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-500">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* AI Insights */}
        <div className="grid grid-cols-4 gap-3">
          {aiInsights.map((insight, i) => (
            <div key={i} className={`${insight.bg} rounded-xl p-3 border border-transparent`}>
              <div className="flex items-center gap-2 mb-1.5">
                <i className={`${insight.icon} ${insight.color} text-sm`}></i>
                <span className={`text-xs font-semibold ${insight.color}`}>{insight.title}</span>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">{insight.content}</p>
            </div>
          ))}
        </div>

        {/* List View */}
        {viewMode === "list" && (
          <>
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 flex-wrap">
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                {categories.map(c => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${category === c ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div className="relative flex-1 min-w-48">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="搜索材料名称/型号..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E8531A]"
                />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {["材料编号", "材料名称", "规格型号", "单位", "当前库存", "最低库存", "单价", "供应商", "最近入库", "操作"].map(h => (
                      <th key={h} className="text-left text-xs font-medium text-gray-500 px-4 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map(item => {
                    const isLow = item.stock <= item.minStock;
                    return (
                      <tr key={item.id} className={`hover:bg-gray-50 transition-colors ${isLow ? "bg-red-50/30" : ""}`}>
                        <td className="px-4 py-3 text-xs font-mono text-gray-500">{item.id}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-800">{item.name}</span>
                            {isLow && <span className="text-[10px] bg-red-100 text-red-500 px-1.5 py-0.5 rounded-full">库存预警</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-gray-600">{item.model}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">{item.unit}</td>
                        <td className="px-4 py-3">
                          <span className={`text-sm font-bold ${isLow ? "text-red-600" : "text-gray-800"}`}>{item.stock.toLocaleString()}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{item.minStock.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-800">¥{item.price.toFixed(2)}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">{item.supplier}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{item.lastIn}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => { setSelectedDevice(deviceModels.find(d => d.category === item.category) || deviceModels[0]); setViewMode("2d"); }}
                              className="text-xs text-[#E8531A] hover:underline cursor-pointer whitespace-nowrap"
                            >
                              2D/3D
                            </button>
                            <button className="text-xs text-gray-500 hover:underline cursor-pointer whitespace-nowrap">出库</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* 2D/3D View */}
        {(viewMode === "2d" || viewMode === "3d") && (
          <div className="grid grid-cols-12 gap-4">
            {/* Device list */}
            <div className="col-span-3 space-y-2">
              <div className="text-xs font-semibold text-gray-500 mb-2">选择设备</div>
              {deviceModels.map(d => (
                <div
                  key={d.id}
                  onClick={() => setSelectedDevice(d)}
                  className={`bg-white rounded-xl border p-3 cursor-pointer transition-all ${selectedDevice.id === d.id ? "border-[#E8531A]/40 bg-orange-50/30" : "border-gray-100 hover:border-gray-200"}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <img src={d.image} alt={d.name} className="w-10 h-10 rounded-lg object-cover" />
                    <div>
                      <div className="text-xs font-semibold text-gray-800">{d.name}</div>
                      <div className="text-[10px] text-gray-400">{d.model}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">{d.category}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${d.status === "正常" ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>{d.status}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* 2D/3D Display */}
            <div className="col-span-5">
              <div className="bg-white rounded-2xl border border-gray-100 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">{selectedDevice.name}</h3>
                    <p className="text-xs text-gray-400">{selectedDevice.model} · {selectedDevice.category}</p>
                  </div>
                  <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setDeviceView("2d")}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${deviceView === "2d" ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500"}`}
                    >
                      2D图纸
                    </button>
                    <button
                      onClick={() => setDeviceView("3d")}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${deviceView === "3d" ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500"}`}
                    >
                      3D模型
                    </button>
                  </div>
                </div>

                {deviceView === "2d" ? (
                  <Device2DView device={selectedDevice} />
                ) : (
                  <Device3DView device={selectedDevice} />
                )}

                <div className="mt-3 grid grid-cols-3 gap-2">
                  {[
                    { label: "库存数量", value: `${selectedDevice.stock}件` },
                    { label: "单价", value: `¥${selectedDevice.price}` },
                    { label: "状态", value: selectedDevice.status },
                  ].map((item, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-2 text-center">
                      <div className="text-xs font-bold text-gray-800">{item.value}</div>
                      <div className="text-[10px] text-gray-400">{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Device detail tabs */}
            <div className="col-span-4">
              <div className="bg-white rounded-2xl border border-gray-100 p-4">
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-4">
                  {([
                    { key: "info", label: "基本信息" },
                    { key: "maintain", label: "维护记录" },
                    { key: "warranty", label: "质保信息" },
                    { key: "history", label: "出入库" },
                  ] as const).map(t => (
                    <button
                      key={t.key}
                      onClick={() => setActiveDeviceTab(t.key)}
                      className={`flex-1 py-1.5 rounded-md text-[10px] font-medium cursor-pointer whitespace-nowrap transition-colors ${activeDeviceTab === t.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500"}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {activeDeviceTab === "info" && (
                  <div className="space-y-2.5">
                    {[
                      { label: "设备编号", value: selectedDevice.id },
                      { label: "设备名称", value: selectedDevice.name },
                      { label: "规格型号", value: selectedDevice.model },
                      { label: "设备类别", value: selectedDevice.category },
                      { label: "存放位置", value: selectedDevice.location },
                      { label: "供应商", value: selectedDevice.supplier },
                      { label: "当前库存", value: `${selectedDevice.stock}件` },
                      { label: "单价", value: `¥${selectedDevice.price}` },
                    ].map((item, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-gray-400">{item.label}</span>
                        <span className="text-gray-700 font-medium">{item.value}</span>
                      </div>
                    ))}
                  </div>
                )}

                {activeDeviceTab === "maintain" && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">下次维护日期</span>
                      <span className="text-xs font-bold text-[#E8531A]">{selectedDevice.maintainDate}</span>
                    </div>
                    <div className="space-y-2">
                      {[
                        { date: "2026-01-15", type: "年度检测", result: "合格", operator: "张工" },
                        { date: "2025-07-20", type: "半年维护", result: "合格", operator: "李工" },
                        { date: "2025-01-10", type: "年度检测", result: "合格", operator: "张工" },
                      ].map((log, i) => (
                        <div key={i} className="bg-gray-50 rounded-lg p-2.5">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-gray-700">{log.type}</span>
                            <span className="text-[10px] bg-green-50 text-green-600 px-1.5 py-0.5 rounded-full">{log.result}</span>
                          </div>
                          <div className="flex items-center gap-2 text-[10px] text-gray-400">
                            <span>{log.date}</span>
                            <span>·</span>
                            <span>{log.operator}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button className="w-full border border-dashed border-gray-200 text-gray-400 py-2 rounded-lg text-xs cursor-pointer hover:border-[#E8531A] hover:text-[#E8531A] transition-colors whitespace-nowrap">
                      <i className="ri-add-line mr-1"></i>添加维护记录
                    </button>
                  </div>
                )}

                {activeDeviceTab === "warranty" && (
                  <div className="space-y-3">
                    <div className="bg-green-50 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-shield-check-line text-green-600"></i>
                        <span className="text-xs font-semibold text-green-700">质保有效期内</span>
                      </div>
                      <div className="text-xs text-gray-600">
                        质保到期：<span className="font-bold text-green-700">{selectedDevice.warranty}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {[
                        { label: "质保类型", value: "整机质保" },
                        { label: "质保期限", value: "3年" },
                        { label: "供应商", value: selectedDevice.supplier },
                        { label: "质保范围", value: "非人为损坏" },
                        { label: "联系方式", value: "400-000-0000" },
                      ].map((item, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="text-gray-400">{item.label}</span>
                          <span className="text-gray-700 font-medium">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeDeviceTab === "history" && (
                  <div className="space-y-2">
                    {[
                      { date: "2026-04-08", type: "入库", qty: 200, operator: "仓管员王芳", note: "正常采购入库" },
                      { date: "2026-04-05", type: "出库", qty: -50, operator: "施工队张工", note: "朝阳区报装施工" },
                      { date: "2026-03-28", type: "入库", qty: 300, operator: "仓管员王芳", note: "补货入库" },
                      { date: "2026-03-20", type: "出库", qty: -80, operator: "维修队李工", note: "设备更换维修" },
                    ].map((log, i) => (
                      <div key={i} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${log.qty > 0 ? "bg-green-100" : "bg-red-100"}`}>
                          <i className={`${log.qty > 0 ? "ri-arrow-down-line text-green-600" : "ri-arrow-up-line text-red-600"} text-xs`}></i>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-700">{log.type}</span>
                            <span className={`text-xs font-bold ${log.qty > 0 ? "text-green-600" : "text-red-600"}`}>{log.qty > 0 ? "+" : ""}{log.qty}</span>
                          </div>
                          <div className="text-[10px] text-gray-400">{log.date} · {log.operator}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* AI Analysis View - Deep */}
        {viewMode === "ai" && <MaterialAIDeep />}
      </div>

      {/* In Modal */}
      {inModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setInModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-[480px]" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-900 mb-4">材料入库登记</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">材料名称 *</label>
                  <input type="text" placeholder="请输入材料名称" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">规格型号</label>
                  <input type="text" placeholder="型号规格" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">入库数量 *</label>
                  <input type="number" placeholder="0" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">单价（元）</label>
                  <input type="number" placeholder="0.00" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">供应商</label>
                <input type="text" placeholder="请输入供应商名称" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">质保到期日</label>
                <input type="date" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => setInModal(false)} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
                <button onClick={() => setInModal(false)} className="flex-1 bg-[#E8531A] text-white py-2 rounded-lg text-sm cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">确认入库</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
